import type {
  BeforeSendFn,
  CapturedNetworkRequest,
  Properties,
} from 'posthog-js'
import posthog from 'posthog-js'

import { ACCESS_LINK_FRAGMENT } from '@/lib/access-link-fragment'
import { redactJsonString } from '@/lib/api-client/redact-sensitive'
import { ENVELOPE_ACCESS_TOKEN_HEADER } from '@/lib/envelope-access'
import { logger } from '@/lib/logger'

const CARD_NUMBER =
  /(4[0-9]{12}(?:[0-9]{3})?)|(5[1-5][0-9]{14})|(6(?:011|5[0-9]{2})[0-9]{12})|(3[47][0-9]{13})|(3(?:0[0-5]|[68][0-9])[0-9]{11})|((?:2131|1800|35[0-9]{3})[0-9]{11})/

const SOCIAL_SECURITY_NUMBER = /\d{3}-?\d{2}-?\d{4}/

const CREDENTIAL_WORDS = [
  'password',
  'secret',
  'passwd',
  'api_key',
  'apikey',
  'auth',
  'credentials',
  'mysql_pwd',
  'privatekey',
  'private_key',
  'token',
]

const SECRET_BEARING_ENDPOINT =
  /\/(access-code|access-links|verifications)(?=[/?#]|$)/

const redactedBody = (
  body: string | null | undefined,
  label: 'Request' | 'Response',
) =>
  body === null || body === undefined
    ? body
    : `[SessionRecording] ${label} body redacted`

const maskBody = (
  body: string | null | undefined,
  label: 'Request' | 'Response',
) => {
  if (body === null || body === undefined) {
    return body
  }

  if (
    CARD_NUMBER.test(body.replace(/[- ]/g, '')) ||
    SOCIAL_SECURITY_NUMBER.test(body)
  ) {
    return `[SessionRecording] ${label} body redacted`
  }

  const credentialWord = CREDENTIAL_WORDS.find((word) => body.includes(word))
  if (credentialWord) {
    return `[SessionRecording] ${label} body redacted as might contain: ${credentialWord}`
  }

  return redactJsonString(body)
}

const withoutEnvelopeAccessToken = (
  headers: CapturedNetworkRequest['requestHeaders'],
): CapturedNetworkRequest['requestHeaders'] =>
  headers &&
  Object.fromEntries(
    Object.entries(headers).filter(
      ([name]) =>
        name.toLowerCase() !== ENVELOPE_ACCESS_TOKEN_HEADER.toLowerCase(),
    ),
  )

const maskCapturedNetworkRequest = (
  request: CapturedNetworkRequest,
): CapturedNetworkRequest => {
  const maskFor = SECRET_BEARING_ENDPOINT.test(request.name)
    ? redactedBody
    : maskBody
  return {
    ...request,
    requestHeaders: withoutEnvelopeAccessToken(request.requestHeaders),
    requestBody: maskFor(request.requestBody, 'Request'),
    responseBody: maskFor(request.responseBody, 'Response'),
  }
}

const withoutAccessLinkFragment = (properties: Properties): Properties =>
  Object.fromEntries(
    Object.entries(properties).map(([key, value]) => [
      key,
      typeof value === 'string'
        ? value.replace(ACCESS_LINK_FRAGMENT, '')
        : value,
    ]),
  )

const dropAccessLinkFragment: BeforeSendFn = (event) =>
  event && {
    ...event,
    properties: withoutAccessLinkFragment(event.properties),
    ...(event.$set && { $set: withoutAccessLinkFragment(event.$set) }),
    ...(event.$set_once && {
      $set_once: withoutAccessLinkFragment(event.$set_once),
    }),
  }

interface PostHogConfig {
  key: string
  host: string
  recordSessions: boolean
  autocapture: boolean
}

const getPostHogConfig = (): PostHogConfig | null => {
  const key = import.meta.env.VITE_POSTHOG_KEY
  const host = import.meta.env.VITE_POSTHOG_HOST
  const recordSessions = import.meta.env.VITE_POSTHOG_RECORD_SESSIONS === 'true'
  const autocapture = import.meta.env.VITE_POSTHOG_AUTOCAPTURE === 'true'

  if (!key) {
    logger.error('PostHog key not found in environment variables')
    return null
  }

  return { key, host, recordSessions, autocapture }
}

export const initPostHog = (): void => {
  const config = getPostHogConfig()

  if (!config) {
    logger.error('PostHog not initialized - missing configuration')
    return
  }

  try {
    posthog.init(config.key, {
      api_host: config.host,
      person_profiles: 'always',
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: config.autocapture,
      disable_capture_url_hashes: true,
      before_send: dropAccessLinkFragment,
      // PostHog covers LCP, CLS, FCP and INP — TTFB is not collected.
      capture_performance: { web_vitals: true },
      opt_in_site_apps: true,
      session_recording: config.recordSessions
        ? {
            recordCrossOriginIframes: true,
            recordHeaders: true,
            recordBody: true,
            maskCapturedNetworkRequestFn: maskCapturedNetworkRequest,
          }
        : undefined,
      advanced_enable_surveys: true,
      capture_exceptions: true,
      debug: false,
    })

    logger.info('PostHog initialized successfully', {
      autocapture: config.autocapture,
      recordSessions: config.recordSessions,
    })
  } catch (error) {
    logger.error('Failed to initialize PostHog', { error })
  }
}

export const posthogInstance = posthog
